import { useMemo, useState } from "react";

import { type ApiError } from "./client";
import { useResource } from "./useResource";
import type { DiffLine } from "../types";

// Wire DTOs for the diff read path (see specifications/diffs-and-pull-requests.md
// and the server route typescript/apps/server/src/http/routes/diffs.ts). These
// live here — not in ./types — to keep the diff surface self-contained.

export type ApiDiffFileStatus = "added" | "deleted" | "modified" | "renamed";

export type ApiDiffSnapshot = {
  readonly base_sha: string;
  readonly files_changed: number;
  readonly head_sha: string;
  readonly lines_added: number;
  readonly lines_removed: number;
  readonly public_id: string;
  // How many mid-turn captures have landed on this row. Compared against a
  // live frame's, so a frame that arrives out of order or after the seal is
  // discarded rather than applied backwards.
  readonly revision: number;
  // False while the turn that is writing this snapshot is still running
  // (§Diff sources). A sealed snapshot never changes again; an open one is
  // what the live frames rewrite.
  readonly sealed: boolean;
};

export type ApiDiffFile = {
  // A sha256 of the file's patch. The only thing on the list that says whether
  // *this* file moved when a snapshot did, which is what the blob fetch below
  // keys on.
  readonly content_sha: string | null;
  readonly lines_added: number;
  readonly lines_removed: number;
  readonly path: string;
  readonly previous_path: string | null;
  readonly status: ApiDiffFileStatus;
};

// GET /tasks/:id/diff — latest snapshot metadata plus its file list.
export type ApiTaskDiff = {
  // Optional because `api.get` asserts this shape onto the response rather
  // than checking it, and `mapTaskDiff` covers a body that arrives without it.
  readonly files?: ReadonlyArray<ApiDiffFile>;
  readonly review: {
    readonly public_id: string;
    readonly touched_paths: ReadonlyArray<string>;
  } | null;
  readonly snapshot: ApiDiffSnapshot | null;
};

// GET /tasks/:id/diff/file?path=… — the file's raw unified-diff text.
export type ApiDiffFileBlob = {
  readonly content_sha: string | null;
  readonly new_source: string | null;
  readonly old_source: string | null;
  readonly patch: string;
  readonly path: string;
};

// View models the dashboard renders against.
export type DiffFileView = {
  readonly contentSha: string | null;
  readonly linesAdded: number;
  readonly linesRemoved: number;
  readonly path: string;
  readonly previousPath: string | null;
  readonly status: ApiDiffFileStatus;
};

export type TaskDiffView = {
  readonly baseSha: string | null;
  readonly files: ReadonlyArray<DiffFileView>;
  readonly filesChanged: number;
  readonly headSha: string | null;
  /**
   * The file the newest revision moved — what Follow keeps in view. Null on a
   * fetched diff: only a live frame says which file an edit landed in, and a
   * revision that moved several names the first in path order.
   */
  readonly lastTouchedPath: string | null;
  readonly linesAdded: number;
  readonly linesRemoved: number;
  /** The snapshot a comment anchors to: the newest *sealed* one. */
  readonly reviewSnapshotId: string | null;
  readonly revision: number;
  readonly sealed: boolean;
  readonly snapshotId: string | null;
  /**
   * Files the open turn has moved since the review anchor. Their lines are
   * being rewritten under the reader, so the gutter composer is inert on them
   * (§Diff comments) — every other file's works as it always did.
   */
  readonly touchedPaths: ReadonlySet<string>;
};

/**
 * A `diff.live.updated` frame (realtime.md §Event kinds).
 *
 * The manifest is **cumulative**, not a delta — the whole current file list, as
 * the streaming caret carries the whole current text — so a dropped frame costs
 * a moment of staleness rather than a permanently wrong tree. `files` is absent
 * on a diff too large for the frame to carry, and its absence is the cue to
 * refetch the list.
 */
export type LiveDiffFrame = {
  readonly files?: ReadonlyArray<ApiDiffFile>;
  readonly files_changed: number;
  readonly head_sha: string;
  readonly lines_added: number;
  readonly lines_removed: number;
  readonly revision: number;
  readonly snapshot_public_id: string;
};

const HUNK_HEADER = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

// Parse raw `git diff` text into the unified-hunk rows the diff view renders
// (the `DiffLine` shape the prototype's `DIFF_HUNK` used). We track each side's
// absolute line number off the hunk header, exactly as the diff gutters show.
export const parsePatch = (patch: string): ReadonlyArray<DiffLine> => {
  const rows: Array<DiffLine> = [];
  let oldLine = 0;
  let newLine = 0;
  for (const raw of patch.split("\n")) {
    const header = HUNK_HEADER.exec(raw);
    if (header) {
      oldLine = Number(header[1]);
      newLine = Number(header[2]);
      rows.push({ text: raw, type: "head" });
      continue;
    }
    // Skip git file headers and the "no newline" marker; a per-file blob may
    // still carry them ahead of the first hunk.
    if (
      raw.startsWith("diff --git") ||
      raw.startsWith("index ") ||
      raw.startsWith("--- ") ||
      raw.startsWith("+++ ") ||
      raw.startsWith("new file") ||
      raw.startsWith("deleted file") ||
      raw.startsWith("rename ") ||
      raw.startsWith("similarity ") ||
      raw.startsWith("\\")
    ) {
      continue;
    }
    const marker = raw[0];
    const src = raw.slice(1);
    if (marker === "+") {
      rows.push({ n2: newLine, src, type: "add" });
      newLine += 1;
    } else if (marker === "-") {
      rows.push({ n1: oldLine, src, type: "del" });
      oldLine += 1;
    } else if (marker === " ") {
      rows.push({ n1: oldLine, n2: newLine, src, type: "ctx" });
      oldLine += 1;
      newLine += 1;
    }
    // A bare "" (trailing newline) or any other line is not part of a hunk.
  }
  return rows;
};

export const mapTaskDiff = (data: ApiTaskDiff): TaskDiffView => ({
  baseSha: data.snapshot?.base_sha ?? null,
  files: (data.files ?? []).map((f) => ({
    contentSha: f.content_sha ?? null,
    linesAdded: f.lines_added,
    linesRemoved: f.lines_removed,
    path: f.path,
    previousPath: f.previous_path,
    status: f.status,
  })),
  filesChanged: data.snapshot?.files_changed ?? (data.files ?? []).length,
  headSha: data.snapshot?.head_sha ?? null,
  lastTouchedPath: null,
  linesAdded: data.snapshot?.lines_added ?? 0,
  linesRemoved: data.snapshot?.lines_removed ?? 0,
  reviewSnapshotId: data.review?.public_id ?? data.snapshot?.public_id ?? null,
  revision: data.snapshot?.revision ?? 0,
  // A server that predates the seal lifecycle sends neither field; treating
  // the snapshot as sealed is the reading that changes nothing.
  sealed: data.snapshot?.sealed ?? true,
  snapshotId: data.snapshot?.public_id ?? null,
  touchedPaths: new Set(data.review?.touched_paths ?? []),
});

/**
 * Apply a live frame to the diff already held.
 *
 * Applied rather than refetched, which is the whole point of the frame
 * carrying the manifest: the five-request fan-out a new snapshot triggers is
 * right once a turn and indefensible every two seconds, per open tab.
 *
 * Returns the diff unchanged when the frame is not news — a different snapshot,
 * a revision already applied, or a frame with no manifest on it, which is the
 * server saying the diff outgrew the wire and the list should be refetched.
 */
export const applyLiveFrame = (
  diff: TaskDiffView | undefined,
  frame: LiveDiffFrame,
): TaskDiffView | undefined => {
  if (
    !diff ||
    !frame.files ||
    (diff.snapshotId !== null &&
      diff.snapshotId !== frame.snapshot_public_id) ||
    frame.revision <= diff.revision
  ) {
    return diff;
  }
  const files = frame.files.map((f) => ({
    contentSha: f.content_sha ?? null,
    linesAdded: f.lines_added,
    linesRemoved: f.lines_removed,
    path: f.path,
    previousPath: f.previous_path ?? null,
    status: f.status,
  }));
  // A file whose patch moved in this frame is one the turn is editing now, so
  // it joins the set the composer stays out of. The set only grows within a
  // turn; the seal is what empties it, by refetching.
  const touched = new Set(diff.touchedPaths);
  const before = new Map(diff.files.map((f) => [f.path, f.contentSha]));
  const movedNow: string[] = [];
  for (const f of files) {
    if (before.get(f.path) !== f.contentSha) {
      touched.add(f.path);
      movedNow.push(f.path);
    }
  }
  return {
    ...diff,
    files,
    filesChanged: frame.files_changed,
    headSha: frame.head_sha,
    lastTouchedPath: movedNow[0] ?? diff.lastTouchedPath,
    linesAdded: frame.lines_added,
    linesRemoved: frame.lines_removed,
    revision: frame.revision,
    sealed: false,
    snapshotId: frame.snapshot_public_id,
    touchedPaths: touched,
  };
};

export type UseTaskDiff = {
  /** Fold a `diff.live.updated` frame into what is rendered. */
  readonly applyFrame: (frame: LiveDiffFrame) => void;
  readonly diff: TaskDiffView | undefined;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useTaskDiff = (taskId: string | null): UseTaskDiff => {
  const { data, error, loading, refetch } = useResource<ApiTaskDiff>(
    taskId ? `/tasks/${taskId}/diff` : null,
  );
  const fetched = useMemo(() => (data ? mapTaskDiff(data) : undefined), [data]);
  // Live frames are held beside the fetched diff rather than merged into it, so
  // a refetch — a seal, a reconnect, a resync — always wins over whatever the
  // frames had built up.
  //
  // Which one is current is *derived*, never reset in an effect. A held value
  // from a turn that has since sealed names a different snapshot and carries a
  // higher revision than the fresh fetch's zero, so a rule that compared only
  // revisions would render the previous turn's tree for a frame before the
  // effect corrected it — and would then apply the new turn's frames onto that
  // stale base, which `applyLiveFrame`'s own snapshot guard would refuse
  // outright.
  const [live, setLive] = useState<TaskDiffView | undefined>(undefined);
  const applied =
    live &&
    fetched &&
    live.snapshotId === fetched.snapshotId &&
    live.revision > fetched.revision
      ? live
      : fetched;
  return {
    // Applied to whatever is current rather than to whatever is held, which is
    // the same thing except after a seal. Two frames landing in one tick both
    // compute from the pre-render value, and that is correct: the manifest is
    // cumulative, so the later one carries the whole truth either way.
    applyFrame: (frame: LiveDiffFrame) =>
      setLive(() => applyLiveFrame(applied, frame)),
    diff: applied,
    error,
    loading,
    refetch,
  };
};

// Files whose unified-diff text exceeds this render inline-diff-free with a
// "download to view" affordance (§Rendering / large-file fallback).
export const MAX_INLINE_DIFF_BYTES = 500 * 1024;

export type UseTaskDiffFile = {
  readonly error: ApiError | undefined;
  readonly lines: ReadonlyArray<DiffLine>;
  readonly loading: boolean;
  readonly newSource: string | null;
  readonly oldSource: string | null;
  // True when the patch is too large to render inline; `patch` still carries the
  // raw text so the card can offer a download.
  readonly oversized: boolean;
  readonly patch: string;
};

// Lazily fetch and parse one file's diff blob. Pass `path === null` to skip
// (e.g. a collapsed card), matching `useResource`'s null-path convention.
//
// `ignoreWhitespace` is a fetch option, not a render flag: the server serves the
// `git diff -w` form of the blob and the whitespace-only hunks never cross the
// wire (specifications/diffs-and-pull-requests.md §Rendering). Toggling it
// changes the query key, so `useResource` refetches the affected blobs.
//
// `contentSha` is in the key for the same reason and is the whole of why it is
// on the file list. The card is keyed by path and stays mounted across
// snapshots, and the route always serves the *current* blob for a path — so
// without the sha in the key, a reader sitting on the Diff tab when a turn ends
// watched the tree, the counts and the totals update above a patch that never
// did. The server ignores the value; it exists to be part of this key.
export const useTaskDiffFile = (
  taskId: string,
  path: string | null,
  ignoreWhitespace: boolean,
  contentSha: string | null,
): UseTaskDiffFile => {
  const { data, error, loading } = useResource<ApiDiffFileBlob>(
    path ? `/tasks/${taskId}/diff/file` : null,
    path
      ? {
          path,
          sha: contentSha ?? undefined,
          whitespace: ignoreWhitespace ? "ignore" : undefined,
        }
      : undefined,
  );
  const patch = data?.patch ?? "";
  const oversized = patch.length > MAX_INLINE_DIFF_BYTES;
  // Skip parsing an oversized patch — it would spawn tens of thousands of rows.
  const lines = useMemo(
    () => (data && !oversized ? parsePatch(data.patch) : []),
    [data, oversized],
  );
  return {
    error,
    lines,
    loading,
    newSource: data?.new_source ?? null,
    oldSource: data?.old_source ?? null,
    oversized,
    patch,
  };
};
